import { useMemo } from "react";
import type { Measurement } from "@care-by-ella/contracts";
import {
  measurementCharts,
  type ChartFrame,
  type DrawnChart,
  type DrawnGroup,
} from "@/lib/measurement-chart-lines";

// TES-1481 (Ö-805) — her weight, waist, blood pressure and pulse as charts,
// grouped and scaled as in her own "Grafer och trender". Drawn from the list
// the correction table below reads, so the two can never disagree, and
// nothing is fetched or logged twice. Inline SVG, like "Mående".
// TES-1510 — blood pressure and pulse are one group, two charts on one time
// axis, as in the app.

const FRAME: ChartFrame = {
  width: 640,
  height: 140,
  padLeft: 88,
  padRight: 16,
  padTop: 12,
  padBottom: 24,
};

function Chart({ chart, caption }: { chart: DrawnChart; caption: boolean }) {
  return (
    <div className="flex flex-col gap-xs">
      {caption ? (
        <span className="text-xs text-textSecondary">{chart.title}</span>
      ) : null}
      <svg
        viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
        className="w-full max-w-2xl"
        role="img"
        aria-label={chart.summary}
      >
        {chart.scale.map((s) => (
          <g key={s.label}>
            <line
              x1={FRAME.padLeft}
              x2={FRAME.width - FRAME.padRight}
              y1={s.y}
              y2={s.y}
              className="text-surfaceMuted"
              stroke="currentColor"
              strokeWidth={1}
            />
            <text
              x={FRAME.padLeft - 8}
              y={s.y}
              textAnchor="end"
              dominantBaseline="middle"
              className="text-textSecondary"
              fill="currentColor"
              fontSize={11}
            >
              {s.label}
            </text>
          </g>
        ))}
        {chart.dates.map((d, i) => (
          <text
            key={d.label}
            x={d.x}
            y={FRAME.height - 6}
            textAnchor={
              chart.dates.length === 1 ? "middle" : i === 0 ? "start" : "end"
            }
            className="text-textSecondary"
            fill="currentColor"
            fontSize={11}
          >
            {d.label}
          </text>
        ))}
        {chart.lines.map((line) => (
          <g
            key={line.key}
            className={line.muted ? "text-textSecondary" : "text-brand"}
          >
            {line.polyline ? (
              <polyline
                points={line.polyline}
                fill="none"
                stroke="currentColor"
                strokeWidth={line.muted ? 1.5 : 2}
              />
            ) : null}
            {line.points.map((p) => (
              <circle
                key={p.id}
                cx={p.x}
                cy={p.y}
                r={line.muted ? 3 : 4}
                fill="currentColor"
              >
                <title>{p.title}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
}

function Group({ group }: { group: DrawnGroup }) {
  // A group of one chart is titled by it; a group of two names each.
  const caption = group.charts.some((c) => c.title !== group.title);
  return (
    <figure className="flex flex-col gap-xs">
      <figcaption className="text-xs font-semibold">{group.title}</figcaption>
      {group.charts.map((c) => (
        <Chart key={c.type} chart={c} caption={caption} />
      ))}
    </figure>
  );
}

export function MeasurementCharts({
  measurements,
}: {
  measurements: Measurement[];
}) {
  const groups = useMemo(
    () => measurementCharts(measurements, FRAME),
    [measurements],
  );
  if (groups.length === 0) return null;
  return (
    <div
      className="mb-md flex flex-col gap-md"
      data-testid="measurement-charts"
    >
      {groups.map((g) => (
        <Group key={g.key} group={g} />
      ))}
    </div>
  );
}
